import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import {
  Box,
  Button,
  ColorPicker,
  FlexBox,
  FormDialog,
  Grid,
  Icon,
  InputField,
  MuiBox,
  SelectDropdown,
  Switch,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { FONTS, Font } from 'constants/fonts';
import { useActions } from 'hooks/useActions';
import { selectors as clientsSelectors, Client } from 'store/clients';
import {
  actions as configActions,
  selectors as configSelectors,
  Configuration,
  UpdateConfigurationPayload
} from 'store/configurations';
import { actions as settingsActions, selectors as settingsSelectors } from 'store/settings';
import { doesFormHaveInitialValues, getUpdatedResourceWithFormValues, onInputBlur, onValueChange } from 'utils/forms';
import { getSortedList } from 'utils/stringFormat';
import { configValidationSchema } from 'views/ConfigurationView/utils';
import { useStyles } from '../styled';
import ManageConfigurationsOverlay from './components/ManageConfigurationsOverlay';
import { ConfigurationForm, ConfigurationsViewProps } from './types';
import { getConfigurationForm } from './utils';

const ConfigurationsView = ({ setHasUnsavedChanges }: ConfigurationsViewProps) => {
  const classes = useStyles();

  // ACTIONS AND SELECTORS
  const { getConfigurations, updateConfiguration, resetConfigurationsAction, setConfigurationId } = useActions({
    getConfigurations: configActions.getConfigurations,
    updateConfiguration: configActions.updateConfiguration,
    resetConfigurationsAction: configActions.resetAction,
    setConfigurationId: settingsActions.setConfigurationId
  });
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const configurations = useSelector(configSelectors.selectConfigurations);
  const sortedConfigurations = useMemo(() => getSortedList(configurations, 'name'), [configurations]);
  const configurationId = useSelector(settingsSelectors.selectConfigurationId);
  const configurationsAction = useSelector(configSelectors.selectAction);

  const initialForm = useMemo(() => getConfigurationForm(), []);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedConfiguration, setSelectedConfiguration] = useState<Configuration | null>(null);
  const [configurationForm, setConfigurationForm] = useState<ConfigurationForm>(initialForm);
  const [resetForm, setResetForm] = useState<ConfigurationForm>(initialForm);
  const [isDisabled, setIsDisabled] = useState(false);
  const [showManageOverlay, setShowManageOverlay] = useState(false);

  const hasInitialValues = useMemo(() => doesFormHaveInitialValues(configurationForm), [configurationForm]);
  const hasInvalidName = !configurationForm.name.value.trim() || configurationForm.name.error !== null;

  const restrictedFonts: string[] = get(client, 'configuration.restrictedFonts', []);

  const filteredFonts: Font[] = React.useMemo(() => {
    const availableFonts = restrictedFonts.includes('*')
      ? FONTS
      : FONTS.filter((font) => !font.isRestricted || restrictedFonts.includes(font.code));

    return getSortedList(availableFonts, 'displayName');
  }, [restrictedFonts]);

  useEffect(() => {
    getConfigurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  useEffect(() => {
    if (sortedConfigurations.length && !configurationId) {
      setConfigurationId(sortedConfigurations[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedConfigurations]);

  useEffect(() => {
    let configuration = null;
    if (configurationId && sortedConfigurations.length) {
      configuration = sortedConfigurations.find((c) => c.id === configurationId);
      if (!configuration) configuration = sortedConfigurations[0];
    }
    setSelectedConfiguration(configuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationId, sortedConfigurations]);

  useEffect(() => {
    if (selectedConfiguration) {
      const form = getConfigurationForm(selectedConfiguration);
      setConfigurationForm(form);
      setResetForm(form);
    }
  }, [selectedConfiguration]);

  useEffect(() => {
    const isEqual = JSON.stringify(configurationForm) === JSON.stringify(resetForm);
    setIsDisabled(isEqual);
    setHasUnsavedChanges(!isEqual);
  }, [configurationForm, resetForm]);

  useEffect(() => {
    if (configurationsAction.success) {
      resetConfigurationsAction();
    }
  }, [configurationsAction.success]);

  // Handle API errors
  useEffect(() => {
    if (configurationsAction.error) {
      // Errors with a known code have a specific message; everything else gets the generic one
      setErrorMessage(
        configurationsAction.errorCode ? configurationsAction.error : 'Uh oh! Something went wrong. Please try again.'
      );
      resetConfigurationsAction();
    }
  }, [configurationsAction.error]);

  const onSaveClick = () => {
    const updatedConfiguration = getUpdatedResourceWithFormValues(
      selectedConfiguration as Configuration,
      configurationForm
    );
    // Specify which keys to update to avoid immutable keys like createdAt and updatedAt
    const payload: UpdateConfigurationPayload = {
      id: updatedConfiguration.id,
      name: updatedConfiguration.name.trim(),
      configuration: updatedConfiguration.configuration,
      ticketing: updatedConfiguration.ticketing
    };
    updateConfiguration(payload);
    setResetForm(configurationForm);
    setHasUnsavedChanges(false);
  };

  const renderFieldLabel = (label: string, readOnly: boolean = false, indented: boolean = false) => (
    <Typography
      className={classes.fieldLabel}
      sx={{
        color: readOnly ? 'grey' : 'black',
        paddingLeft: indented ? '16px' : '0px'
      }}
    >
      {label}
    </Typography>
  );

  return (
    <>
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage('')}
          confirmButtonAction={() => setErrorMessage('')}
          confirmButtonText="Ok"
          open={Boolean(errorMessage)}
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
      {/* Header */}
      <Grid className={classes.headerBlock}>
        <Box flexOverride="none">
          <Typography className={classes.headerText}>Configurations</Typography>
        </Box>
        {/* Configuration selection */}
        {/* Label and gear sit beside the dropdown, or above it on smaller screens */}
        <MuiBox
          display="grid"
          alignItems="center"
          gap="10px"
          sx={{
            gridTemplateAreas: { xs: '"label gear" "dropdown dropdown"', md: '"label dropdown gear"' },
            gridTemplateColumns: { xs: '1fr auto', md: 'auto auto auto' }
          }}
        >
          <Typography
            sx={{ gridArea: 'label', fontSize: '16px', fontWeight: 'bold', paddingLeft: { xs: '16px', md: 0 } }}
          >
            Configuration
          </Typography>
          <MuiBox gridArea="dropdown">
            <SelectDropdown
              items={sortedConfigurations}
              itemToString={(item: Configuration) => item && item.name}
              selectedItem={selectedConfiguration as Configuration}
              onChange={(item: Configuration) => setConfigurationId(item.id)}
              radius="round"
              width="262px"
              sx={{
                boxShadow: 'none',
                height: '45px',
                '& .MuiSelect-select': {
                  color: selectedConfiguration ? '#000' : '#9E9E9E'
                }
              }}
              data-testid="configuration-selector"
            />
          </MuiBox>
          <MuiBox gridArea="gear" display="flex" marginRight={{ xs: '10px', md: 0 }}>
            <Tooltip title="Manage Configurations" arrow placement="top" verticalOffset={-8}>
              <Icon
                name="Settings"
                sx={{ cursor: 'pointer', display: 'block' }}
                onClick={() => setShowManageOverlay(true)}
                data-testid="manage-configurations-button"
              />
            </Tooltip>
          </MuiBox>
        </MuiBox>
      </Grid>
      {/* Bottom section (settings and save button) */}
      <FlexBox padding="0 7 7" noWrap direction="column">
        {/* Container of settings */}
        <FlexBox align="center" margin="0 0 5" direction="row">
          <Box
            raised
            width="100%"
            minHeight="572px"
            flexOverride="none"
            padding="24px"
            sx={{ borderRadius: '4px', overflowY: 'auto' }}
          >
            <Grid container spacing={2} alignItems="center" rowGap={2}>
              {/* Name */}
              <Grid item xs={12} sm={4}>
                {renderFieldLabel('Name', configurationForm.name.readOnly)}
              </Grid>
              <Grid item xs={12} sm={8}>
                <InputField
                  data-testid="configuration-name-field"
                  disabled={configurationForm.name.readOnly}
                  error={configurationForm.name.error !== null}
                  helperText={configurationForm.name.error}
                  inputProps={{ maxLength: 255 }}
                  onBlur={(e) => onInputBlur(e, 'name', configValidationSchema, setConfigurationForm)}
                  onChange={(e) => onValueChange(e.target.value, 'name', setConfigurationForm)}
                  size="small"
                  sx={{ width: '262px', height: '45px' }}
                  value={configurationForm.name.value}
                />
              </Grid>
              {/* Primary Color */}
              <Grid item xs={12} sm={4}>
                {renderFieldLabel('Primary Color', configurationForm.primaryColor.readOnly)}
              </Grid>
              <Grid item xs={12} sm={8}>
                <ColorPicker
                  value={configurationForm.primaryColor.value}
                  onChange={(color) => onValueChange(color, 'primaryColor', setConfigurationForm)}
                  disabled={configurationForm.primaryColor.readOnly}
                />
              </Grid>
              {/* Primary Font */}
              <Grid item xs={12} sm={4}>
                {renderFieldLabel('Primary Font', configurationForm.primaryFont.readOnly)}
              </Grid>
              <Grid item xs={12} sm={8}>
                <SelectDropdown
                  items={filteredFonts}
                  selectedItem={filteredFonts.find((f) => f.code === configurationForm.primaryFont.value) || null}
                  itemToString={(item) => (item ? item.displayName : '')}
                  onChange={(item) => onValueChange(item.code, 'primaryFont', setConfigurationForm)}
                  placeholder={'Select Primary Font'}
                  radius="round"
                  width="262px"
                  sx={{
                    height: '45px',
                    '& .MuiSelect-select': {
                      color: configurationForm.primaryFont.value ? '#000' : '#9E9E9E'
                    }
                  }}
                  data-testid={'primary-font-selector'}
                />
              </Grid>
              {/* Donation Prompt */}
              <Grid item xs={12} sm={4}>
                {renderFieldLabel('Donation Prompt', configurationForm.donationPopup.readOnly)}
              </Grid>
              <Grid item xs={12} sm={8}>
                <Switch
                  checked={configurationForm.donationPopup.value === true}
                  checkedLabel="Enabled"
                  disabled={configurationForm.donationPopup.readOnly}
                  uncheckedLabel="Disabled"
                  containerStyle={{ width: '130px' }}
                  onChange={(e) => onValueChange(e.target.checked, 'donationPopup', setConfigurationForm)}
                  data-testid="donation-roundup-switch"
                />
              </Grid>
              {/* Customer ID */}
              <Grid item xs={12}>
                {renderFieldLabel('Customer ID')}
              </Grid>
              {/* Customer ID - Lookup */}
              <Grid item xs={12} sm={4}>
                {renderFieldLabel('Lookup', configurationForm.lookup.readOnly, true)}
              </Grid>
              <Grid item xs={12} sm={8}>
                <Switch
                  checked={configurationForm.lookup.value === true}
                  checkedLabel="Enabled"
                  uncheckedLabel="Disabled"
                  disabled={configurationForm.lookup.readOnly}
                  containerStyle={{ width: '130px' }}
                  onChange={(e) => onValueChange(e.target.checked, 'lookup', setConfigurationForm)}
                  data-testid="customer-lookup-switch"
                />
              </Grid>
              {/* Customer ID - Creation */}
              <Grid item xs={12} sm={4}>
                {renderFieldLabel('Creation', configurationForm.creation.readOnly, true)}
              </Grid>
              <Grid item xs={12} sm={8}>
                <Switch
                  checked={configurationForm.creation.value === true}
                  checkedLabel="Enabled"
                  uncheckedLabel="Disabled"
                  disabled={configurationForm.creation.readOnly}
                  containerStyle={{ width: '130px' }}
                  onChange={(e) => onValueChange(e.target.checked, 'creation', setConfigurationForm)}
                  data-testid="customer-creation-switch"
                />
              </Grid>
              {/* Survey */}
              <Grid item xs={12} sm={4}>
                {renderFieldLabel('Survey', configurationForm.surveyPage.readOnly)}
              </Grid>
              <Grid item xs={12} sm={8}>
                <Switch
                  checked={configurationForm.surveyPage.value === true}
                  checkedLabel="Enabled"
                  disabled={configurationForm.surveyPage.readOnly}
                  uncheckedLabel="Disabled"
                  containerStyle={{ width: '130px' }}
                  onChange={(e) => onValueChange(e.target.checked, 'surveyPage', setConfigurationForm)}
                  data-testid="survey-switch"
                />
              </Grid>
            </Grid>
          </Box>
        </FlexBox>
        {/* Save button */}
        <FlexBox sx={{ justifyContent: 'flex-end', gap: '10px' }}>
          <Button customSize="small" width={144} onClick={() => setConfigurationForm(resetForm)} disabled={isDisabled}>
            Reset
          </Button>
          <Button
            customSize="small"
            width={144}
            disabled={hasInitialValues || !selectedConfiguration || hasInvalidName}
            onClick={onSaveClick}
          >
            Save
          </Button>
        </FlexBox>
      </FlexBox>
      {showManageOverlay && <ManageConfigurationsOverlay onClose={() => setShowManageOverlay(false)} />}
    </>
  );
};

export default ConfigurationsView;
